/**
 * Per-agent run-health derivation (#275).
 *
 * Pure, side-effect-free (no React / UI imports) so it is trivially unit-testable. The
 * agents list turns each agent's MOST RECENT run status (`AgentView.lastRunStatus`) into
 * a compact health indicator — flagging a blocked/failing agent and positively confirming
 * a healthy one. Tones mirror the `RunStatusBadge` conventions (danger = failure,
 * success = terminal-good, info = in-flight, warning = needs attention, neutral =
 * inactive/none) so state reads consistently across the app.
 */

import type { RunStatus } from '@app/shared';

/** Semantic tone — a subset of the `@/ui` `StatusBadge` tones. */
export type RunHealthTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export interface RunHealth {
  tone: RunHealthTone;
  /** Short label for the badge, e.g. "Healthy", "Failing", "No runs yet". */
  label: string;
  /**
   * True when the last run failed/blocked (FAILED / BLOCKED / LIMIT_EXCEEDED) — the row
   * is danger-highlighted so a broken agent visibly stands out.
   */
  failing: boolean;
}

/**
 * Map a most-recent-run status to a health indicator. `null` (the agent has never run)
 * → a muted, neutral "No runs yet". Exhaustive over `RunStatus`: the `satisfies never`
 * guard makes a newly-added run status a compile error here until it is classified.
 */
export function deriveRunHealth(status: RunStatus | null): RunHealth {
  switch (status) {
    // Failure — flag the agent (danger tone + row highlight).
    case 'FAILED':
    case 'LIMIT_EXCEEDED':
      return { tone: 'danger', label: 'Failing', failing: true };
    case 'BLOCKED':
      return { tone: 'danger', label: 'Blocked', failing: true };
    // Terminal-good — positive confirmation.
    case 'COMPLETED':
    case 'LIMIT_REACHED':
      return { tone: 'success', label: 'Healthy', failing: false };
    // In-flight.
    case 'QUEUED':
    case 'RUNNING':
    case 'WAITING':
      return { tone: 'info', label: 'Running', failing: false };
    // Needs attention.
    case 'WAITING_FOR_APPROVAL':
      return { tone: 'warning', label: 'Needs approval', failing: false };
    // Inactive.
    case 'CANCELLED':
      return { tone: 'neutral', label: 'Cancelled', failing: false };
    // Never ran.
    case null:
      return { tone: 'neutral', label: 'No runs yet', failing: false };
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
